import {
    useCallback,
    useEffect,
    useState,
    useRef,
    type FC,
    useId
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ICitationProps from "./type";
import type {
    CitationKeyboardEventType,
    CitationWebPropsType,
    ISource
} from "./type";
import stylesheet, {
    getCitationMeasures,
    getCitationColors,
    useStyles
} from "./stylesheet";
import CitationSourceCard from "./internal/sourceCard";
import {
    getSourceDomain,
    getSourceDate,
    openSourceUrl
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    ExternalLink as ExternalLinkIcon,
    Link as LinkIcon
} from "lucide-react-native";
import {
    getPopoverTriggerElement,
    handlePopoverTabKeyDown,
    getPopoverTabbables,
    focusPopoverElement,
    getPopoverElement
} from "../popover/util";
import {
    copyTextToClipboard
} from "../../utils";
import ActionSheet from "../bottomSheet/components/actionSheet";
import PopoverSurface from "../popoverSurface";
import StateLayer from "../stateLayer";
import Text from "../text";

const Citation: FC<ICitationProps> = ({
    "aria-label": ariaLabelProp,
    isDisabled = false,
    variant = "number",
    customLocalize,
    sources = [],
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    index,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const baseId = useId();

    const anchorRef = useRef<View>(null);

    const surfaceRef = useRef<View>(null);

    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isDismissedRef = useRef(false);

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        isSheetOpen,
        setIsSheetOpen
    ] = useState(false);

    const [
        isAutoFocused,
        setIsAutoFocused
    ] = useState(false);

    const [
        sourceIndex,
        setSourceIndex
    ] = useState(0);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isTouch = Platform.OS !== "web" || pointerType === "coarse";

    const isAvailable = !isDisabled && sources.length > 0;

    const isIntent = (isHovered || isFocusVisible) && isAvailable && !isTouch;

    const currentMeasures = getCitationMeasures({
        touchTargets,
        pointerType,
        typography,
        spaces
    });

    const currentColors = getCitationColors({
        isOpen: isOpen || isSheetOpen,
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        currentMeasures,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        isDisabled,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const handleClearOpenTimer = useCallback(() => {
        if(openTimerRef.current === null) {
            return;
        }

        clearTimeout(openTimerRef.current);

        openTimerRef.current = null;
    }, []);

    const handleCancelClose = useCallback(() => {
        if(closeTimerRef.current === null) {
            return;
        }

        clearTimeout(closeTimerRef.current);

        closeTimerRef.current = null;
    }, []);

    const handleClose = useCallback(() => {
        handleClearOpenTimer();

        handleCancelClose();

        setIsOpen(false);
    }, [
        handleClearOpenTimer,
        handleCancelClose
    ]);

    const handleScheduleOpen = useCallback(() => {
        handleCancelClose();

        if(openTimerRef.current !== null) {
            return;
        }

        openTimerRef.current = setTimeout(() => {
            openTimerRef.current = null;

            setIsOpen(true);
        }, durations.hoverOpen);
    }, [
        handleCancelClose,
        durations.hoverOpen
    ]);

    const handleScheduleClose = useCallback(() => {
        handleClearOpenTimer();

        if(closeTimerRef.current !== null) {
            return;
        }

        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;

            setIsOpen(false);
        }, durations.hoverClose);
    }, [
        handleClearOpenTimer,
        durations.hoverClose
    ]);

    const handleDismiss = () => {
        isDismissedRef.current = true;

        const surfaceElement = getPopoverElement(surfaceRef.current);

        const anchorElement = getPopoverElement(anchorRef.current);

        if(anchorElement && surfaceElement?.contains(surfaceElement.ownerDocument.activeElement)) {
            focusPopoverElement(getPopoverTriggerElement(anchorElement) ?? anchorElement);
        }

        handleClose();
    };

    useEffect(() => {
        if(isIntent) {
            if(!isDismissedRef.current) {
                handleScheduleOpen();
            }

            return;
        }

        isDismissedRef.current = false;

        if(!isOpen) {
            handleClearOpenTimer();

            return;
        }

        if(!isAutoFocused) {
            handleScheduleClose();
        }
    }, [
        isIntent
    ]);

    useEffect(() => {
        return () => {
            handleClearOpenTimer();

            handleCancelClose();
        };
    }, [
        handleClearOpenTimer,
        handleCancelClose
    ]);

    useEffect(() => {
        if(isOpen || isSheetOpen) {
            return;
        }

        setIsAutoFocused(false);

        setSourceIndex(0);
    }, [
        isSheetOpen,
        isOpen
    ]);

    const handlePress = () => {
        if(isDisabled) {
            return;
        }

        onPress?.();

        if(!isAvailable) {
            return;
        }

        if(isTouch) {
            setIsSheetOpen(true);

            return;
        }

        handleClearOpenTimer();

        handleCancelClose();

        setIsAutoFocused(true);

        setIsOpen(true);
    };

    const handleOpenSource = (source: ISource) => {
        openSourceUrl({
            source
        });

        setIsSheetOpen(false);
    };

    const handleCopyLink = (source: ISource) => {
        copyTextToClipboard(source.url).catch(() => undefined);
    };

    const onKeyDown = (event: CitationKeyboardEventType) => {
        const surfaceElement = getPopoverElement(surfaceRef.current);

        if(event.key !== "Tab" || !isOpen || !surfaceElement) {
            return;
        }

        const [
            firstElement
        ] = getPopoverTabbables(surfaceElement);

        if(!firstElement) {
            return;
        }

        event.preventDefault();

        focusPopoverElement(firstElement);
    };

    const onSurfaceKeyDown = (event: CitationKeyboardEventType) => {
        handlePopoverTabKeyDown({
            triggerRef: anchorRef,
            onLeave: handleClose,
            surfaceRef,
            event
        });
    };

    const getAriaLabel = (): string | undefined => {
        if(ariaLabelProp) {
            return ariaLabelProp;
        }

        const [
            firstSource
        ] = sources;

        const indexText = index === undefined ? undefined : `${index}`;

        if(isDisabled) {
            return localize("source-x-removed", [
                indexText ?? ""
            ]);
        }

        if(!firstSource) {
            return indexText ? localize("source-x", [
                indexText
            ]) : localize("source");
        }

        const domain = getSourceDomain({
            source: firstSource
        });

        if(variant === "source" && sources.length > 1) {
            return localize("x-sources-domain-and-more", [
                sources.length,
                domain,
                sources.length - 1
            ]);
        }

        const subject = variant === "source" ? domain : firstSource.title;

        if(!indexText) {
            return `${localize("source")}: ${subject}`;
        }

        return localize("source-x-title", [
            indexText,
            subject
        ]);
    };

    const getLabel = (): string => {
        if(variant === "number") {
            return `${index ?? sources.length}`;
        }

        const [
            firstSource
        ] = sources;

        if(!firstSource) {
            return `${index ?? ""}`;
        }

        const domain = getSourceDomain({
            source: firstSource
        });

        return sources.length > 1 ? `${domain} +${sources.length - 1}` : domain;
    };

    const renderOpenIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <ExternalLinkIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const renderLinkIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <LinkIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const renderSheet = () => {
        if(!isTouch || !isAvailable) {
            return null;
        }

        const currentSource = sources[0];

        if(!currentSource) {
            return null;
        }

        const date = getSourceDate({
            locale: activeLocale,
            source: currentSource
        });

        const domain = getSourceDomain({
            source: currentSource
        });

        if(sources.length === 1) {
            return <ActionSheet
                items={[
                    {
                        onPress: () => handleOpenSource(currentSource),
                        label: localize("open-source"),
                        icon: renderOpenIcon,
                        key: "open"
                    },
                    {
                        onPress: () => handleCopyLink(currentSource),
                        label: localize("copy-link"),
                        icon: renderLinkIcon,
                        key: "copy"
                    }
                ]}
                description={date ? `${domain} · ${date}` : domain}
                onClosed={() => setIsSheetOpen(false)}
                customLocalize={customLocalize}
                title={currentSource.title}
                customTheme={customTheme}
                isActive={isSheetOpen}
            >
                {
                    currentSource.snippet ?
                        <Text
                            customTheme={customTheme}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {currentSource.snippet}
                        </Text>
                    :
                        null
                }
            </ActionSheet>;
        }

        return <ActionSheet
            items={sources.map((source, sourcesIndex) => ({
                onPress: () => handleOpenSource(source),
                key: `${sourcesIndex}`,
                meta: getSourceDomain({
                    source
                }),
                icon: renderOpenIcon,
                label: source.title
            }))}
            onClosed={() => setIsSheetOpen(false)}
            customLocalize={customLocalize}
            title={localize("source")}
            customTheme={customTheme}
            isActive={isSheetOpen}
        />;
    };

    const renderCard = () => {
        if(isTouch || !isAvailable) {
            return null;
        }

        const webProps: CitationWebPropsType = {
            onKeyDown: onSurfaceKeyDown
        };

        return <PopoverSurface
            {...webProps}
            aria-label={localize("source-preview")}
            onPointerLeave={handleScheduleClose}
            collisionPadding={spaces.spacingMd}
            onPointerEnter={handleCancelClose}
            width={shapeConfigs.cardWidth}
            isAutoFocused={isAutoFocused}
            onFocus={handleCancelClose}
            returnFocusRef={anchorRef}
            customTheme={customTheme}
            onDismiss={handleDismiss}
            id={`${baseId}-card`}
            anchorRef={anchorRef}
            padding="spacingMd"
            ref={surfaceRef}
            isOpen={isOpen}
            role="dialog"
        >
            <CitationSourceCard
                onSourceIndexChange={setSourceIndex}
                customLocalize={customLocalize}
                onOpenPress={handleOpenSource}
                sourceIndex={sourceIndex}
                customTheme={customTheme}
                sources={sources}
            />
        </PopoverSurface>;
    };

    const webProps: CitationWebPropsType = Platform.OS === "web" ? {
        onKeyDown
    } : {};

    return <>
        <Pressable
            {...props}
            {...webProps}
            {...interactionHandlers}
            aria-controls={Platform.OS === "web" && isOpen ? `${baseId}-card` : undefined}
            aria-expanded={isOpen || isSheetOpen}
            aria-label={getAriaLabel()}
            aria-disabled={isDisabled}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            aria-haspopup="dialog"
            disabled={isDisabled}
            onPress={handlePress}
            ref={anchorRef}
            role="button"
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    isHovered={!isDisabled && isHovered}
                    isPressed={!isDisabled && isPressed}
                    borderRadius={radiuses.chip}
                    borderWidth={borders.line}
                    customTheme={customTheme}
                    overlayColor="neutral"
                />
                <Text
                    customColor={currentColors.titleColor}
                    customTheme={customTheme}
                    style={stylesheet.label}
                    variant="labelSmallSize"
                    numberOfLines={1}
                >
                    {getLabel()}
                </Text>
            </View>
        </Pressable>
        {renderCard()}
        {renderSheet()}
    </>;
};
export default Citation;
